import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Star, Award, CheckCircle, Users, TrendingUp } from 'lucide-react';

const profilesData = [
  {
    id: 1,
    name: 'Marco Tanaka',
    avatar: '/api/placeholder/64/64',
    credScore: 85,
    badge: 'Recensore Accreditato',
    reviews: 47,
    pillarsStrong: ['義', '誠', '清'],
    transparency: {
      sourcing: true,
      waste: true,
      accessibility: false
    },
    verifiedBadges: [
      { name: 'Esperto Sushi', issuer: 'FIC', verified: true },
      { name: 'Sommelier Sake', issuer: 'IBA', verified: true }
    ]
  },
  {
    id: 2,
    name: 'Elena Bushido',
    avatar: '/api/placeholder/64/64',
    credScore: 92,
    badge: 'Maestro Recensore',
    reviews: 128,
    pillarsStrong: ['仁', '礼', '忠義'],
    transparency: {
      sourcing: true,
      waste: true,
      accessibility: true
    },
    verifiedBadges: [
      { name: 'Critico Gastronomico', issuer: 'FIPE', verified: true },
      { name: 'Esperto Sostenibilità', issuer: 'Slow Food', verified: true },
      { name: 'Accessibilità Expert', issuer: 'ANMIC', verified: true }
    ]
  },
  {
    id: 3,
    name: 'Giovanni Samurai',
    avatar: '/api/placeholder/64/64',
    credScore: 78,
    badge: 'Recensore Accreditato',
    reviews: 23,
    pillarsStrong: ['勇', '清'],
    transparency: {
      sourcing: true,
      waste: false,
      accessibility: true
    },
    verifiedBadges: [
      { name: 'Chef Professionista', issuer: 'FIC', verified: true }
    ]
  }
];

const badgeTypes = [
  {
    name: 'Recensore Accreditato',
    description: 'Ha superato il quiz di accreditamento BushiFood',
    criteria: 'Quiz 10/12, CredScore ≥60',
    color: 'bg-blue-600'
  },
  {
    name: 'Maestro Recensore',
    description: 'Recensore esperto con alta credibilità',
    criteria: '50+ recensioni, CredScore ≥90',
    color: 'bg-purple-600'
  },
  {
    name: 'Esperto Sushi',
    description: 'Competenza verificata nella cucina giapponese',
    criteria: 'Certificazione FIC, 20+ recensioni sushi',
    color: 'bg-red-600'
  },
  {
    name: 'Sommelier Sake',
    description: 'Conoscenza approfondita dei sake giapponesi',
    criteria: 'Certificazione IBA, esame teorico-pratico',
    color: 'bg-green-600'
  },
  {
    name: 'Critico Gastronomico',
    description: 'Professionista riconosciuto nel settore',
    criteria: 'Iscrizione FIPE, pubblicazioni verificate',
    color: 'bg-yellow-600'
  },
  {
    name: 'Esperto Sostenibilità',
    description: 'Competenza in pratiche sostenibili',
    criteria: 'Certificazione Slow Food, formazione specifica',
    color: 'bg-emerald-600'
  }
];

export default function ProfilesPage() {
  const [selectedTab, setSelectedTab] = useState('profiles');

  const getCredScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  const getCredScoreLabel = (score: number) => {
    if (score >= 90) return 'Eccellente';
    if (score >= 70) return 'Buono';
    if (score >= 50) return 'Sufficiente';
    return 'Da Migliorare';
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-bushido text-4xl font-bold mb-4">
            Profili & <span className="gold-accent">Badge</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Sistema di trasparenza e credibilità per recensori. 
            Badge verificati da enti riconosciuti e trasparenza attiva sui criteri di valutazione.
          </p>
        </div>

        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profiles" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Profili Recensori</span>
            </TabsTrigger>
            <TabsTrigger value="badges" className="flex items-center space-x-2">
              <Award className="h-4 w-4" />
              <span>Sistema Badge</span>
            </TabsTrigger>
            <TabsTrigger value="transparency" className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>Trasparenza</span>
            </TabsTrigger>
          </TabsList>

          {/* Profili Recensori */}
          <TabsContent value="profiles" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {profilesData.map((profile) => (
                <Card key={profile.id} className="pillar-card">
                  <CardHeader className="pb-4">
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={profile.avatar} alt={profile.name} />
                        <AvatarFallback className="bg-secondary text-secondary-foreground font-bold">
                          {profile.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-bushido text-lg font-semibold">
                          {profile.name}
                        </h3>
                        <Badge className={`${profile.badge === 'Maestro Recensore' ? 'bg-purple-600' : 'bg-blue-600'} text-white`}>
                          {profile.badge}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* CredScore */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">CredScore</span>
                        <span className={`font-bold ${getCredScoreColor(profile.credScore)}`}>
                          {profile.credScore}/100
                        </span>
                      </div>
                      <Progress value={profile.credScore} className="h-2" />
                      <div className="text-xs text-muted-foreground text-right">
                        {getCredScoreLabel(profile.credScore)}
                      </div>
                    </div>

                    {/* Statistiche */}
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <div className="text-lg font-bold text-secondary">{profile.reviews}</div>
                        <div className="text-xs text-muted-foreground">Recensioni</div>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <div className="text-lg font-bold text-secondary">{profile.verifiedBadges.length}</div>
                        <div className="text-xs text-muted-foreground">Badge Verificati</div>
                      </div>
                    </div>

                    {/* Pilastri Forti */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Pilastri di Forza</h4>
                      <div className="flex space-x-2">
                        {profile.pillarsStrong.map((kanji, index) => (
                          <Badge key={index} variant="secondary" className="font-kanji text-lg px-3 py-1">
                            {kanji}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Trasparenza */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Trasparenza Attiva</h4>
                      <div className="flex space-x-2">
                        {profile.transparency.sourcing && (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Sourcing
                          </Badge>
                        )}
                        {profile.transparency.waste && (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Rifiuti
                          </Badge>
                        )}
                        {profile.transparency.accessibility && (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Accessibilità
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Badge Verificati */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Badge Verificati</h4>
                      <div className="space-y-1">
                        {profile.verifiedBadges.map((badge, index) => (
                          <div key={index} className="flex items-center justify-between text-xs">
                            <span>{badge.name}</span>
                            <div className="flex items-center space-x-1">
                              <span className="text-muted-foreground">{badge.issuer}</span>
                              {badge.verified && <CheckCircle className="h-3 w-3 text-green-500" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Sistema Badge */}
          <TabsContent value="badges" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {badgeTypes.map((badge, index) => (
                <Card key={index} className="pillar-card">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className={`${badge.color} p-3 rounded-full`}>
                        <Award className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{badge.name}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm leading-relaxed">
                      {badge.description}
                    </CardDescription>
                    
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-secondary">Criteri di Ottenimento</h4>
                      <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                        {badge.criteria}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border-secondary/30">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Trophy className="mr-2 h-5 w-5 text-accent" />
                  Come Ottenere Badge Verificati
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Enti Riconosciuti</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">FIC</Badge>
                        <span>Federazione Italiana Cuochi</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">IBA</Badge>
                        <span>International Bartenders Association</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">FIPE</Badge>
                        <span>Federazione Italiana Pubblici Esercizi</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">Slow Food</Badge>
                        <span>Movimento Internazionale</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold">Processo di Verifica</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                        <span>Richiesta con documentazione</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                        <span>Verifica da parte dell'ente</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                        <span>Approvazione e attivazione badge</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-secondary rounded-full"></div>
                        <span>Rinnovo periodico (se richiesto)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trasparenza */}
          <TabsContent value="transparency" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Metadati di Trasparenza</CardTitle>
                  <CardDescription>
                    Informazioni che i recensori possono condividere volontariamente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <h4 className="font-medium">Sourcing Ingredienti</h4>
                        <p className="text-sm text-muted-foreground">Provenienza e qualità</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <h4 className="font-medium">Gestione Rifiuti</h4>
                        <p className="text-sm text-muted-foreground">Sostenibilità e sprechi</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <h4 className="font-medium">Accessibilità</h4>
                        <p className="text-sm text-muted-foreground">Inclusività e barriere</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Indicatori di Qualità</CardTitle>
                  <CardDescription>
                    Metriche utilizzate per valutare recensioni e recensori
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">BushiScore</span>
                        <span className="text-sm text-muted-foreground">1-100</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Punteggio complessivo basato sui 7 Pilastri
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Trasparenza Attiva</span>
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Percentuale di campi compilati + evidenze
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Consistenza Narrativa</span>
                        <span className="text-sm text-muted-foreground">σ</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Varianza tra punteggi e note descrittive
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium">Affidabilità Autore</span>
                        <span className="text-sm text-muted-foreground">CredScore</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Credibilità basata su storia e badge
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-gradient-to-r from-muted/50 to-muted/30 border-secondary/30">
              <CardHeader>
                <CardTitle>Principi di Trasparenza BushiFood</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center">
                      <span className="font-kanji text-secondary mr-2">誠</span>
                      Onestà Totale
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Tutti i criteri di valutazione sono pubblici e verificabili. 
                      Nessun algoritmo nascosto, nessuna manipolazione dei punteggi.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center">
                      <span className="font-kanji text-secondary mr-2">義</span>
                      Giustizia Procedurale
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Ogni recensore ha pari opportunità di ottenere badge e credibilità. 
                      I criteri sono gli stessi per tutti, senza eccezioni.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}